/**
 * Sprach-Umschalter im App-Header.
 *
 * Schreibt `i18next.changeLanguage()` + persistiert den Wert im
 * localStorage, sodass eine manuelle Wahl Vorrang vor der Shopify-
 * Admin-Sprache bekommt (siehe `detection.order` in `config.ts`).
 */
import { useState, type MouseEvent } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { Language, Check } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const LANGUAGES: Array<{ code: string; label: string; native: string }> = [
  { code: "de", label: "Deutsch", native: "DE" },
  { code: "en", label: "English", native: "EN" },
  { code: "nl", label: "Nederlands", native: "NL" },
  { code: "fr", label: "Français", native: "FR" },
  { code: "it", label: "Italiano", native: "IT" },
  { code: "pl", label: "Polski", native: "PL" },
  { code: "es", label: "Español", native: "ES" },
];

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation("common");
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const current = (i18n.resolvedLanguage ?? i18n.language ?? "de")
    .toLowerCase()
    .split("-")[0];
  const currentLang =
    LANGUAGES.find((l) => l.code === current) ?? LANGUAGES[0];

  function open(e: MouseEvent<HTMLElement>) {
    setAnchor(e.currentTarget);
  }
  function close() {
    setAnchor(null);
  }
  async function pick(code: string) {
    close();
    await i18n.changeLanguage(code);
  }

  return (
    <>
      <Tooltip title={t("language.switcher")}>
        <IconButton
          onClick={open}
          size="small"
          sx={{ color: "text.primary", gap: 0.5 }}
        >
          <Language fontSize="small" />
          <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.04em" }}>
            {currentLang.native}
          </span>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        slotProps={{ paper: { sx: { minWidth: 160 } } }}
      >
        {LANGUAGES.map((l) => (
          <MenuItem
            key={l.code}
            selected={l.code === current}
            onClick={() => void pick(l.code)}
          >
            <ListItemIcon sx={{ minWidth: 30 }}>
              {l.code === current ? <Check fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText
              primary={l.label}
              secondary={l.native}
              primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
              secondaryTypographyProps={{ fontSize: 11, letterSpacing: "0.06em" }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
